import {useCallback, useEffect, useMemo, useRef} from "react";
import axios from "axios";

const CHAT_SYNC_LAST_UPDATE = "2023-01-01";
const POLL_INTERVAL_MS = 2000;

const fetchNewestMessageId = async ({chatUrl, group, login}) => {
    const {data} = await axios.get(
        `${chatUrl}api/v1/chat.syncMessages?roomId=${group}&lastUpdate=${CHAT_SYNC_LAST_UPDATE}`,
        {headers: {"X-User-Id": login.userId, "X-Auth-Token": login.authToken}},
    );
    const updated = data?.result?.updated ?? [];
    return updated[0]?._id ?? null;
};

const useDashboardChatWatch = ({authUrlFor, openAttendanceId, activeChatIds = [], onNewMessage}) => {
    const watchedRef = useRef(new Map());
    const openIdRef = useRef(null);
    const prevOpenIdRef = useRef(null);
    const activeIdsRef = useRef(new Set());
    const onNewMessageRef = useRef(onNewMessage);

    useEffect(() => { onNewMessageRef.current = onNewMessage; }, [onNewMessage]);
    useEffect(() => { activeIdsRef.current = new Set(activeChatIds.map(String)); }, [activeChatIds]);

    useEffect(() => {
        const nextOpen = openAttendanceId ? String(openAttendanceId) : null;
        const prevOpen = prevOpenIdRef.current;
        prevOpenIdRef.current = nextOpen;
        openIdRef.current = nextOpen;

        if (prevOpen && prevOpen !== nextOpen && watchedRef.current.has(prevOpen)) {
            const entry = watchedRef.current.get(prevOpen);
            fetchNewestMessageId(entry.credentials)
                .then(newestId => {
                    if (watchedRef.current.has(prevOpen)) watchedRef.current.set(prevOpen, {...entry, lastReadId: newestId});
                })
                .catch(() => {});
        }
    }, [openAttendanceId]);

    const trackChat = useCallback(async attendanceId => {
        const id = attendanceId ? String(attendanceId) : null;
        if (!id) return;

        try {
            const {data: credentials} = await axios.get(authUrlFor(id));
            if (!credentials?.chatUrl || !credentials?.group || !credentials?.login) return;
            const newestId = await fetchNewestMessageId(credentials).catch(() => null);
            watchedRef.current.set(id, {credentials, lastReadId: newestId});
        } catch {
            watchedRef.current.delete(id);
        }
    }, [authUrlFor]);

    useEffect(() => {
        const interval = setInterval(() => {
            const openId = openIdRef.current;
            const activeIds = activeIdsRef.current;

            watchedRef.current.forEach((entry, attendanceId) => {
                if (!activeIds.has(attendanceId)) {
                    watchedRef.current.delete(attendanceId);
                    return;
                }
                if (attendanceId === openId) return;

                fetchNewestMessageId(entry.credentials)
                    .then(newestId => {
                        if (newestId && newestId !== entry.lastReadId) onNewMessageRef.current?.(attendanceId);
                    })
                    .catch(() => watchedRef.current.delete(attendanceId));
            });
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, []);

    return useMemo(() => ({trackChat}), [trackChat]);
};

export default useDashboardChatWatch;
